import { ErrorResponse, Notification, ServiceResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';

export type NotificationContextType = {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: ErrorResponse | null;
  actions: {
    fetchNotifications: (mode?: string) => Promise<ServiceResponse<Notification[]>>;
    readNotification: (ids: string[]) => Promise<ServiceResponse<any>>;
  };
};

export const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  actions: {
    fetchNotifications: async () => ({ type: 'client-error', message: 'Not implemented' }),
    readNotification: async () => ({ type: 'client-error', message: 'Not implemented' }),
  },
} as NotificationContextType);

export const useNotificationContext = () => {
  return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const { bzbsService } = useBuzzebeesServiceContext();

  const fetchNotifications = async (mode?: string): Promise<ServiceResponse<Notification[]>> => {
    setIsLoading(true);
    const response = await bzbsService.notificationApi.notifications({
      mode: mode ?? 'all',
      sortBy: 'createdate_desc',
    });

    setIsLoading(false);

    if (response.type === 'success') {
      setNotifications(response.model);
      setUnreadCount(response.model.filter((notification: Notification) => !notification.IsRead).length);
    } else {
      setError(response);
    }

    return response;
  };

  const readNotification = async (ids: string[]): Promise<ServiceResponse<any>> => {
    setIsLoading(true);
    const response = await bzbsService.notificationApi.read({ ids: ids.join(',') });
    setIsLoading(false);
    if (response.type === 'success') {
      setNotifications(
        notifications.map((notification) => {
          if (ids.includes(notification.RowKey ?? '')) {
            notification.IsRead = true;
          }
          return notification;
        })
      );
      setUnreadCount(unreadCount - ids.length);
      setIsLoading(false);
    }

    return response;
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        error,
        actions: {
          fetchNotifications,
          readNotification,
        },
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
